// src/notifications/sidebarIndicators.js
// Pure helpers (no React / Redux) so they are trivially unit-testable.

export const INDICATOR = {
  MESSAGES: "messages",
  APPOINTMENTS: "appointments",
  WAITING_ROOM: "waitingRoom",
};

/** Which indicators each role is allowed to see. */
export const ROLE_INDICATORS = {
  patient: [INDICATOR.MESSAGES, INDICATOR.APPOINTMENTS],
  doctor: [INDICATOR.MESSAGES, INDICATOR.APPOINTMENTS, INDICATOR.WAITING_ROOM],
};

const metaOf = (n) => n?.metadata || n?.data || n?._raw?.metadata || {};
const lower = (v) => String(v ?? "").toLowerCase();

const haystack = (n) =>
  `${lower(n?.type)} ${lower(n?.event)} ${lower(metaOf(n)?.event)}`;

export const normId = (v) => {
  if (v == null) return null;
  let s = String(v).trim();
  if (!s) return null;
  if (/^(dr_|pat_)/i.test(s)) s = s.replace(/^(dr_|pat_)/i, "");
  s = s.trim();
  return !s || ["nan", "undefined", "null"].includes(s.toLowerCase()) ? null : s;
};

const sameId = (a, b) => {
  const x = normId(a);
  const y = normId(b);
  if (!x || !y) return false;
  if (x === y) return true;
  const nx = Number(x);
  const ny = Number(y);
  return Number.isFinite(nx) && Number.isFinite(ny) && nx === ny;
};

/**
 * Map a raw notification to a sidebar indicator key (or null).
 * Order matters: waiting-room events are often named "appointment.*", and
 * data-access requests are typed "appointment" in this codebase, so they must
 * be excluded explicitly.
 *
 * ASSUMPTION: the backend emits waiting-room arrivals with an event/type
 * containing "waiting", "check_in" or "patient.arrived|entered".
 */
export function classifyNotification(n) {
  if (!n) return null;
  const h = haystack(n);

  if (h.includes("data_access")) return null;
  if (/waiting|check[_-]?in|patient\.(arrived|entered)/.test(h)) {
    return INDICATOR.WAITING_ROOM;
  }
  if (/message|chat/.test(h)) return INDICATOR.MESSAGES;
  if (/appointment|rendezvous|rdv/.test(h)) return INDICATOR.APPOINTMENTS;
  return null;
}

export const isUnread = (n) => !(n?.read || n?.isRead || n?.readAt);

export const getNotifId = (n) => {
  const id = String(n?.id || n?._id || "").trim();
  return id || null;
};

/** Recipient check: only count what is addressed to the current user. */
const isForUser = (n, userId) => {
  const recipient = n?.userId ?? n?.toUserId ?? n?.to ?? metaOf(n)?.recipientId;
  if (recipient == null || recipient === "") return true; // server already scoped it
  return sameId(recipient, userId);
};

/** Sub-profile scoping: main profile sees non-sub-profile events only. */
const matchesProfile = (n, subprofileId) => {
  const sp = n?.subprofileId ?? n?.subProfileId ?? metaOf(n)?.subprofileId ?? metaOf(n)?.subProfileId;
  const hasSp = sp != null && String(sp).trim() !== "";
  if (!subprofileId) return !hasSp;
  return hasSp && sameId(sp, subprofileId);
};

/**
 * @returns {{ counts: Record<string, number>, idsByKey: Record<string, string[]> }}
 */
export function computeIndicators({ items, role, userId, subprofileId = null }) {
  const allowed = ROLE_INDICATORS[role] || [];
  const counts = {};
  const idsByKey = {};
  allowed.forEach((k) => {
    counts[k] = 0;
    idsByKey[k] = [];
  });

  if (!Array.isArray(items)) return { counts, idsByKey };

  const seen = new Set();
  for (const n of items) {
    if (!isUnread(n)) continue;
    const key = classifyNotification(n);
    if (!key || !allowed.includes(key)) continue;
    if (userId && !isForUser(n, userId)) continue;
    if (role === "patient" && !matchesProfile(n, subprofileId)) continue;

    const id = getNotifId(n);
    if (id) {
      if (seen.has(id)) continue; // socket + REST fallback can both deliver
      seen.add(id);
      idsByKey[key].push(id);
    }
    counts[key] += 1;
  }
  return { counts, idsByKey };
}

/** Route-prefix match on a segment boundary (case-insensitive). */
export const isUnderRoute = (pathname, route) => {
  const p = lower(pathname);
  const base = lower(route).split("?")[0].replace(/\/+$/, "");
  return !!base && (p === base || p.startsWith(`${base}/`));
};